/**
 * Column sorting for the AI Analysis findings table.
 *
 * Kept out of the component so the comparison rules are testable on their own: the values behind the cells are
 * not what the cells print (Ri renders as "2" but sorts as a number, the scab chip sorts by how much attention
 * the area needs, not alphabetically), and getting that wrong is silent.
 */
import { SCAB, parseMaskPoints, relativeArea, stripCat, stripRi } from './findingsMetrics.js';

/** Scab recommendation order: least to most attention. Alphabetical happens to match today; a new value would break it. */
export const SCAB_RANK = Object.freeze({ [SCAB.NO]: 0, [SCAB.POSSIBLE]: 1, [SCAB.YES]: 2 });

export const SORT_ASC = 'asc';
export const SORT_DESC = 'desc';

/** A finite number, or null when the cell has nothing to compare. */
function num(value) {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** A case-insensitive string, or null when the cell is empty. */
function text(value) {
  const trimmed = String(value === null || value === undefined ? '' : value).trim();
  return trimmed ? trimmed.toLowerCase() : null;
}

/**
 * The comparable value behind one cell, or null for "no value". `imageSize` is only needed by the area column,
 * which is derived from the mask polygon rather than stored.
 */
export function sortValue(finding, key, imageSize = {}) {
  if (!finding) return null;
  switch (key) {
    case 'id':
      return num(finding.badgeIndex);
    case 'location':
      return text(finding.locationDescription);
    case 'ri':
      return num(stripRi(finding.coatingBreakdown));
    case 'cat':
      return text(stripCat(finding.pittingCategory));
    case 'conf':
      return num(finding.detectionConfidence);
    case 'area':
      return relativeArea(parseMaskPoints(finding.maskPoints), imageSize.width, imageSize.height);
    case 'scab': {
      const rank = SCAB_RANK[String(finding.scabMeasure || '').toUpperCase()];
      return rank === undefined ? null : rank;
    }
    case 'scabconf':
      return num(finding.scabConfidence);
    case 'notes':
      return text(finding.scabReason || finding.description);
    case 'component':
      return text(finding.masterTagName);
    default:
      return null;
  }
}

/** Badge order is the report's natural order and breaks every tie, so equal cells never shuffle between sorts. */
function tieBreak(a, b) {
  return (Number(a.badgeIndex) || 0) - (Number(b.badgeIndex) || 0);
}

/**
 * A new array sorted by one column. Rows with no value in that column sort last in BOTH directions - flipping
 * the direction should reorder the data, not push the blanks to the top.
 */
export function sortFindings(findings, key, direction = SORT_ASC, sizeById = {}) {
  const rows = [...(findings || [])];
  if (!key) return rows;

  const sign = direction === SORT_DESC ? -1 : 1;
  const values = new Map(rows.map((f) => [f.id, sortValue(f, key, sizeById[f.imageId] || {})]));

  return rows.sort((a, b) => {
    const left = values.get(a.id);
    const right = values.get(b.id);
    if (left === null && right === null) return tieBreak(a, b);
    if (left === null) return 1;
    if (right === null) return -1;

    const diff =
      typeof left === 'number' && typeof right === 'number' ? left - right : String(left).localeCompare(String(right));
    return diff !== 0 ? sign * diff : tieBreak(a, b);
  });
}
